import { useState, useEffect, useRef } from 'react';
import { testSupabaseConnection } from '../utils/supabaseStorage';

export interface NetworkStatus {
  isOnline: boolean;
  isConnected: boolean; // More specific - can reach backend
  connectionType: 'wifi' | 'cellular' | 'ethernet' | 'unknown';
  lastConnectedAt?: Date;
  lastDisconnectedAt?: Date;
  retryCount: number;
}

export interface NetworkStatusHook {
  status: NetworkStatus;
  forceCheck: () => Promise<void>;
  isChecking: boolean;
}

export const useNetworkStatus = (): NetworkStatusHook => {
  const [status, setStatus] = useState<NetworkStatus>({
    isOnline: navigator.onLine,
    isConnected: false,
    connectionType: 'unknown',
    retryCount: 0
  });
  const [isChecking, setIsChecking] = useState(false);
  const retryCountRef = useRef(0);
  const lastCheckRef = useRef<Date | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const isMountedRef = useRef(true);

  // Get connection type if available
  const getConnectionType = (): 'wifi' | 'cellular' | 'ethernet' | 'unknown' => {
    if ('connection' in navigator) {
      const connection = (navigator as any).connection;
      if (connection) {
        switch (connection.effectiveType) {
          case 'slow-2g':
          case '2g':
          case '3g':
          case '4g':
            return 'cellular';
          case 'wifi':
            return 'wifi';
          default:
            return 'unknown';
        }
      }
    }
    return 'unknown';
  };

  // Check backend connectivity
  const checkBackendConnection = async (): Promise<boolean> => {
    try {
      const result = await testSupabaseConnection();
      return result.connected;
    } catch (error) {
      // Silence errors if component is unmounting (HMR or cleanup)
      if (isMountedRef.current) {
        console.warn('[NetworkStatus] Backend connection check failed:', error);
      }
      return false;
    }
  };

  // Comprehensive network check
  const performNetworkCheck = async (): Promise<void> => {
    if (isChecking || !isMountedRef.current) return;
    
    // Abort any pending requests
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    // Create new abort controller for this check
    abortControllerRef.current = new AbortController();
    
    setIsChecking(true);
    const now = new Date();
    
    try {
      const isOnline = navigator.onLine;
      const isConnected = isOnline ? await checkBackendConnection() : false;
      const connectionType = getConnectionType();
      
      // Only update state if still mounted
      if (!isMountedRef.current) return;
      
      setStatus(prevStatus => {
        const newStatus: NetworkStatus = {
          isOnline,
          isConnected,
          connectionType,
          retryCount: retryCountRef.current,
          lastConnectedAt: isConnected ? now : prevStatus.lastConnectedAt,
          lastDisconnectedAt: !isConnected ? now : prevStatus.lastDisconnectedAt
        };
        
        // Reset retry count on successful connection
        if (isConnected && !prevStatus.isConnected) {
          retryCountRef.current = 0;
          newStatus.retryCount = 0;
          console.log('[NetworkStatus] Connection restored');
        }
        
        // Increment retry count on failed connection
        if (!isConnected && prevStatus.isConnected) {
          retryCountRef.current++;
          newStatus.retryCount = retryCountRef.current;
          console.log('[NetworkStatus] Connection lost');
        }
        
        return newStatus;
      });
      
      lastCheckRef.current = now;
    } catch (error) {
      // Silence errors if component is unmounting (HMR or cleanup)
      if (isMountedRef.current) {
        console.error('[NetworkStatus] Network check failed:', error);
      }
      
      // Only update state if still mounted
      if (!isMountedRef.current) return;
      
      retryCountRef.current++;
      
      setStatus(prevStatus => ({
        ...prevStatus,
        isOnline: false,
        isConnected: false,
        retryCount: retryCountRef.current,
        lastDisconnectedAt: now
      }));
    } finally {
      if (isMountedRef.current) {
        setIsChecking(false);
      }
    }
  };

  // Initialize network monitoring
  useEffect(() => {
    // Mark as mounted
    isMountedRef.current = true;
    
    // Initial check
    performNetworkCheck();
    
    // Set up network event listeners
    const handleOnline = () => {
      console.log('[NetworkStatus] Browser online event');
      setTimeout(performNetworkCheck, 1000); // Delay to ensure network is really ready
    };
    
    const handleOffline = () => {
      console.log('[NetworkStatus] Browser offline event');
      setStatus(prevStatus => ({
        ...prevStatus,
        isOnline: false,
        isConnected: false,
        lastDisconnectedAt: new Date()
      }));
    };
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    // Set up periodic checks
    intervalRef.current = setInterval(() => {
      const now = new Date();
      const lastCheck = lastCheckRef.current;
      
      // Only check if we haven't checked recently (avoid spam)
      if (!lastCheck || now.getTime() - lastCheck.getTime() > 30000) {
        performNetworkCheck();
      }
    }, 30000); // Check every 30 seconds
    
    // Set up connection change listener if available
    if ('connection' in navigator) {
      const connection = (navigator as any).connection;
      if (connection) {
        const handleConnectionChange = () => {
          console.log('[NetworkStatus] Connection change detected');
          setTimeout(performNetworkCheck, 500);
        };
        
        connection.addEventListener('change', handleConnectionChange);
        
        return () => {
          isMountedRef.current = false;
          window.removeEventListener('online', handleOnline);
          window.removeEventListener('offline', handleOffline);
          connection.removeEventListener('change', handleConnectionChange);
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
          }
          if (abortControllerRef.current) {
            abortControllerRef.current.abort();
          }
        };
      }
    }
    
    return () => {
      isMountedRef.current = false;
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Handle visibility change - check connection when app becomes visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Check connection when app becomes visible
        setTimeout(performNetworkCheck, 500);
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Force check function
  const forceCheck = async (): Promise<void> => {
    await performNetworkCheck();
  };

  return {
    status,
    forceCheck,
    isChecking
  };
};

// Utility hook for simple online/offline status
export const useOnlineStatus = (): boolean => {
  const { status } = useNetworkStatus();
  return status.isOnline;
};

// Utility hook for backend connectivity
export const useBackendConnection = (): boolean => {
  const { status } = useNetworkStatus();
  return status.isConnected;
};

export default useNetworkStatus; 